import { syncContractDatesFromAccommodation } from './contract-dates';

const CHECK_SVG =
    '<svg width="10" height="10" viewBox="0 0 10 10" fill="none">' +
    '<path d="M1.5 5L4 7.5 8.5 2" stroke="white" stroke-width="1.8" ' +
    'stroke-linecap="round" stroke-linejoin="round"/></svg>';

interface WizardState {
    step: number;
    done: Set<number>;
}

export function setupWizard(form: HTMLFormElement): void {
    const wizard = form.closest<HTMLElement>('.cc-wizard');
    if (!wizard) return;

    const state: WizardState = { step: 1, done: new Set() };

    // ── DOM helpers ──

    const panel = (n: number) => form.querySelector<HTMLElement>(`[data-panel="${n}"]`);

    function stepBtn(n: number): HTMLButtonElement | null {
        return wizard.querySelector<HTMLButtonElement>(`[data-step="${n}"]`);
    }

    // ── Render ──

    function syncProgressBar(): void {
        wizard.querySelectorAll<HTMLButtonElement>('[data-step]').forEach(btn => {
            const s = parseInt(btn.dataset.step ?? '0', 10);
            const isActive = s === state.step;
            btn.classList.toggle('active', isActive);
            btn.classList.toggle('done', state.done.has(s) && !isActive);
            btn.setAttribute('aria-current', isActive ? 'step' : 'false');

            const numEl = btn.querySelector<HTMLElement>('.cc-step-num');
            if (!numEl) return;
            if (state.done.has(s) && !isActive) {
                numEl.innerHTML = CHECK_SVG;
            } else {
                numEl.textContent = String(s);
            }
        });
    }

    function showPanel(n: number): void {
        form.querySelectorAll<HTMLElement>('[data-panel]').forEach(p => {
            p.classList.add('d-none');
        });
        const target = panel(n);
        if (target) {
            target.classList.remove('d-none');
            wizard.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        state.step = n;
        syncProgressBar();
    }

    // ── Helpers ──

    function classifyCard(card: HTMLElement): 'complete' | 'empty' | 'partial' {
        const from = card.querySelector<HTMLInputElement>('.sub-range-from-picker')?.value;
        const to   = card.querySelector<HTMLInputElement>('.sub-range-to-picker')?.value;
        if (from && to)   return 'complete';
        if (!from && !to) return 'empty';
        return 'partial';
    }

    // ── Validation ──

    function clearErrors(panelEl: HTMLElement): void {
        panelEl.querySelectorAll<HTMLElement>('[data-wizard-err]').forEach(e => e.remove());
        panelEl.querySelectorAll<HTMLElement>('[aria-invalid="true"]').forEach(el => {
            el.removeAttribute('aria-invalid');
            el.classList.remove('is-invalid');
        });
    }

    function attachClearHandler(field: HTMLElement, errEl: HTMLElement): void {
        const clear = () => {
            field.removeAttribute('aria-invalid');
            field.classList.remove('is-invalid');
            errEl.remove();
        };
        field.addEventListener('input', clear, { once: true });
        field.addEventListener('change', clear, { once: true });
    }

    function showFieldError(field: HTMLElement, message: string): HTMLElement {
        field.setAttribute('aria-invalid', 'true');
        field.classList.add('is-invalid');
        const err = document.createElement('div');
        err.className = 'cc-field-error';
        err.setAttribute('data-wizard-err', '1');
        err.textContent = '⚠ ' + message;
        field.insertAdjacentElement('afterend', err);
        attachClearHandler(field, err);
        return err;
    }

    function showBlockError(container: HTMLElement, message: string): void {
        const existing = container.querySelector<HTMLElement>('[data-wizard-err]');
        if (existing) { existing.textContent = '⚠ ' + message; return; }
        const err = document.createElement('div');
        err.className = 'cc-field-error mt-1';
        err.setAttribute('data-wizard-err', '1');
        err.textContent = '⚠ ' + message;
        container.insertAdjacentElement('beforebegin', err);
    }

    function validateStep(n: number): boolean {
        const panelEl = panel(n);
        if (!panelEl) return true;
        clearErrors(panelEl);

        if (n === 1) {
            let valid = true;

            const officeSelect = form.querySelector<HTMLSelectElement>('#OfficeId');
            if (officeSelect && !officeSelect.value) {
                showFieldError(officeSelect, 'Please select an office');
                valid = false;
            }

            const accommodationPrices = form.querySelector<HTMLElement>('#accommodationPricesContent');
            const hasSubRangeSelection = Array.from(
                form.querySelectorAll<HTMLElement>('#accommodationPricesContent .cc-unit-selection-card')
            ).some(card => {
                const from = card.querySelector<HTMLInputElement>('.sub-range-from-picker')?.value;
                const to   = card.querySelector<HTMLInputElement>('.sub-range-to-picker')?.value;
                return !!(from && to);
            });
            const hasFlatSelection = !!form.querySelector<HTMLInputElement>(
                '#accommodationPricesContent input[name$=".SelectedOfferPriceIds"]:checked'
            );
            if (!hasSubRangeSelection && !hasFlatSelection && accommodationPrices) {
                showBlockError(accommodationPrices, 'Please select an accommodation and pick check-in / check-out dates.');
                valid = false;
            }

            const p1 = panel(1);
            if (p1) {
                p1.querySelectorAll<HTMLElement>('.cc-unit-selection-card').forEach(card => {
                    if (classifyCard(card) !== 'partial') return;
                    const fromInput = card.querySelector<HTMLInputElement>('.sub-range-from-picker');
                    const toInput   = card.querySelector<HTMLInputElement>('.sub-range-to-picker');
                    const emptyInput = (!fromInput?.value ? fromInput : toInput) ?? fromInput;
                    if (emptyInput) showFieldError(emptyInput, 'Please fill both dates or clear this card.');
                    valid = false;
                });
            }

            if (!valid) {
                (officeSelect ?? accommodationPrices)?.scrollIntoView({ block: 'center', behavior: 'smooth' });
            }
            return valid;
        }

        if (n === 2) {
            let valid = true;
            const required: [string, string][] = [
                ['CheckInDate',    'Check-in Date is required'],
                ['CheckOutDate',   'Check-out Date is required'],
                ['NumberOfGuests', 'Number of Guests is required'],
                ['PaymentDueDate', 'Payment Due Date is required'],
            ];
            let firstInvalid: HTMLElement | null = null;
            for (const [id, msg] of required) {
                const el = form.querySelector<HTMLInputElement>(`#${id}`);
                if (el && !el.value) {
                    showFieldError(el, msg);
                    if (!firstInvalid) firstInvalid = el;
                    valid = false;
                }
            }
            const ci = form.querySelector<HTMLInputElement>('#CheckInDate');
            const co = form.querySelector<HTMLInputElement>('#CheckOutDate');
            if (ci?.value && co?.value && new Date(co.value) <= new Date(ci.value)) {
                showFieldError(co, 'Check-out must be after check-in');
                if (!firstInvalid) firstInvalid = co;
                valid = false;
            }
            firstInvalid?.scrollIntoView({ block: 'center', behavior: 'smooth' });
            return valid;
        }

        if (n === 3) {
            let valid = true;
            const fn = form.querySelector<HTMLInputElement>('input[name="ContractOwner.FirstName"]');
            const ln = form.querySelector<HTMLInputElement>('input[name="ContractOwner.LastName"]');
            if (fn && !fn.value.trim()) { showFieldError(fn, 'First Name is required'); valid = false; }
            if (ln && !ln.value.trim()) { showFieldError(ln, 'Last Name is required'); valid = false; }
            if (!valid) fn?.scrollIntoView({ block: 'center', behavior: 'smooth' });
            return valid;
        }

        return true;
    }

    // ── Review ──

    function buildReview(): void {
        const panelEl = panel(4);
        if (!panelEl) return;
        const target = panelEl.querySelector<HTMLElement>('[data-cc-review-target]');
        if (!target) return;

        const val = (id: string) =>
            (form.querySelector<HTMLInputElement>(`#${id}`))?.value?.trim() || '—';

        const selectedText = (id: string) =>
            (form.querySelector<HTMLSelectElement>(`#${id}`))?.selectedOptions[0]?.textContent?.trim() || '';

        const officeName   = selectedText('OfficeId') || '—';
        const accommName   = selectedText('AccommodationOfferIds') || '—';
        const transportName = selectedText('TransportOfferIds');
        const insuranceName = selectedText('InsuranceOfferIds');

        const checkIn   = val('CheckInDate');
        const checkOut  = val('CheckOutDate');
        const guests    = val('NumberOfGuests');
        const payDue    = val('PaymentDueDate');
        const discount  = val('Discount');

        const ownerFn = form.querySelector<HTMLInputElement>('input[name="ContractOwner.FirstName"]')?.value?.trim() || '';
        const ownerLn = form.querySelector<HTMLInputElement>('input[name="ContractOwner.LastName"]')?.value?.trim() || '';
        const ownerName = [ownerFn, ownerLn].filter(Boolean).join(' ') || 'Not filled in';

        const extraCards = form.querySelectorAll<HTMLElement>('#passengersContainer .traveller-card');
        const travellers: string[] = [ownerName];
        extraCards.forEach((card, i) => {
            const fnInput = card.querySelector<HTMLInputElement>('[name$=".FirstName"]');
            const lnInput = card.querySelector<HTMLInputElement>('[name$=".LastName"]');
            const name = [fnInput?.value?.trim(), lnInput?.value?.trim()].filter(Boolean).join(' ');
            travellers.push(name || `Traveller ${i + 2}`);
        });

        const row = (label: string, val: string) =>
            `<div class="cc-review-row"><span class="cc-review-label">${label}</span>` +
            `<span class="cc-review-val">${val || '—'}</span></div>`;

        const block = (title: string, rows: string) =>
            `<div class="cc-review-block"><div class="cc-review-block-title">${title}</div>${rows}</div>`;

        target.innerHTML =
            block('Accommodation', row('Office', officeName) + row('Property', accommName)) +
            (transportName ? block('Transport', row('Provider', transportName)) : '') +
            (insuranceName ? block('Insurance', row('Provider', insuranceName)) : '') +
            block('Contract Details',
                row('Check-in', checkIn) +
                row('Check-out', checkOut) +
                row('Guests', guests) +
                row('Payment due', payDue) +
                row('Discount', discount + '%')
            ) +
            block(
                `Travellers (${travellers.length})`,
                travellers.map((name, i) => row(i === 0 ? 'Owner' : `Traveller ${i + 1}`, name)).join('')
            );
    }

    // ── Navigation ──

    function goTo(n: number): void {
        if (n < 1 || n > 4) return;
        if (n === 4) buildReview();
        showPanel(n);
    }

    function goNext(): void {
        if (!validateStep(state.step)) return;
        state.done.add(state.step);
        if (state.step === 1) {
            const pricesContent = document.getElementById('accommodationPricesContent');
            if (pricesContent) {
                syncContractDatesFromAccommodation(
                    pricesContent,
                    document.getElementById('CheckInDate')  as HTMLInputElement | null,
                    document.getElementById('CheckOutDate') as HTMLInputElement | null,
                );
            }
        }
        goTo(state.step + 1);
    }

    function goPrev(): void {
        goTo(state.step - 1);
    }

    // ── Event listeners ──

    // Progress step clicks
    wizard.querySelectorAll<HTMLButtonElement>('[data-step]').forEach(btn => {
        btn.addEventListener('click', () => {
            const s = parseInt(btn.dataset.step ?? '0', 10);
            // Allow jumping back to completed step or stepping forward through done steps
            if (s < state.step || state.done.has(s)) goTo(s);
        });
    });

    // Back / Continue / Save Draft via event delegation on form
    form.addEventListener('click', e => {
        const t = e.target as HTMLElement;
        if (t.closest('[data-cc-back]'))       { e.preventDefault(); goPrev(); }
        else if (t.closest('[data-cc-continue]')) { e.preventDefault(); goNext(); }
        else if (t.closest('[data-cc-save-draft]')) {
            e.preventDefault();
        }
    });

    // Prune phantom/empty selections before POST; block on partial cards.
    form.addEventListener('submit', e => {
        let hasPartial = false;

        form.querySelectorAll<HTMLElement>('.cc-unit-selection-card').forEach(card => {
            const state = classifyCard(card);
            if (state === 'partial') {
                hasPartial = true;
                const fromInput = card.querySelector<HTMLInputElement>('.sub-range-from-picker');
                const toInput   = card.querySelector<HTMLInputElement>('.sub-range-to-picker');
                const emptyInput = (!fromInput?.value ? fromInput : toInput) ?? fromInput;
                if (emptyInput) showFieldError(emptyInput, 'Please fill both dates or clear this card.');
            } else if (state === 'empty') {
                card.querySelectorAll<HTMLInputElement>('input').forEach(inp => { inp.disabled = true; });
            }
        });

        form.querySelectorAll<HTMLElement>('.cc-unit-prices').forEach(unit => {
            const hasChecked = !!unit.querySelector<HTMLInputElement>('.offer-price-cb:checked');
            if (!hasChecked) {
                unit.querySelectorAll<HTMLInputElement>('input[type="hidden"]').forEach(inp => { inp.disabled = true; });
            }
        });

        if (hasPartial) {
            e.preventDefault();
            goTo(1);
        }
    });

    // Init
    showPanel(1);
}
